import { useMemo } from "react";
import { FoundOperation } from "api/generatedApi";
import { OperationsGroup, OperationItem } from "api/generatedApi";


export interface TreeNode {
  id: string;
  name: string;
  type: "group" | "operation";
  children?: TreeNode[];
  operationData?: OperationItem;
}

export interface UseOperationTreeDataProps {
  operationGroups: OperationsGroup[];
  operations: OperationItem[];
  searchResults: FoundOperation[];
}

export function useOperationTreeData(props: UseOperationTreeDataProps) {
  const { operationGroups, operations, searchResults } = props;

  const treeData = useMemo(() => {
    // Step 1: Filter operations based on search results
    const filteredOperations = searchResults.length > 0 ? operations.filter((op) => searchResults.some((sr) => sr.operationNumber === op.operationNumber)) : operations;


    // Step 2: Get all group IDs that contain filtered operations (case-insensitive)
    const activeGroupIds = new Set<string>();
    filteredOperations.forEach((op) => {
      if (op.operationGroupId) {
        activeGroupIds.add(op.operationGroupId.toLowerCase());
      }
    });

    // Step 3: Add parent groups recursively
    const addParentGroups = (groupId: string) => {
      const normalizedGroupId = groupId.toLowerCase();
      const group = operationGroups.find(
        (g) => g.operationGroupId?.toLowerCase() === normalizedGroupId);

      if (group?.operationGroupParentId) {
        activeGroupIds.add(group.operationGroupParentId.toLowerCase());
        addParentGroups(group.operationGroupParentId);
      }
    };

    activeGroupIds.forEach((groupId) => addParentGroups(groupId));

    // Step 4: Build tree structure
    const buildTree = (parentId: string | null): TreeNode[] => {
      const normalizedParentId = parentId?.toLowerCase() ?? null;

      // Get child groups
      const childGroups = operationGroups
        .filter((g) => (normalizedParentId === null ? g.operationGroupParentId === null : g.operationGroupParentId?.toLowerCase() === normalizedParentId))
        .filter((g) => {
          if (searchResults.length === 0) {
            return true;
          }

          const groupId = g.operationGroupId?.toLowerCase();
          return groupId ? activeGroupIds.has(groupId) : false;
        })
        .reduce<TreeNode[]>((acc, group) => {
          const groupId = group.operationGroupId;
          if (!groupId) {
            return acc;
          }

          const normalizedGroupId = groupId.toLowerCase();
          const children = buildTree(groupId);

          const groupOperations = filteredOperations
            .filter((op) => op.operationGroupId && op.operationGroupId.toLowerCase() === normalizedGroupId)
            .map(
              (op): TreeNode => ({
                id: op.operationId ?? "",
                name: op.operationText ?? "",
                type: "operation" as const,
                operationData: {
                  operationId: op.operationId,
                  operationNumber: op.operationNumber,
                  operationText: op.operationText,
                  operationTime: op.operationTime,
                },
              })
            );

          const allChildren = [...children, ...groupOperations];
          if (allChildren.length === 0) {
            return acc;
          }

          acc.push({
            id: groupId,
            name: group.operationGroupName ?? "",
            type: "group" as const,
            children: allChildren,
          });

          return acc;
        }, []); // Only include groups with children

      return childGroups;
    };

    // Find all root groups (operationGroupNumber === null && operationGroupParentId === null)
    const rootGroups = operationGroups.filter((g) => g.operationGroupNumber === null && g.operationGroupParentId === null);


    // Build tree starting from children of each root (skip the first level)
    if (rootGroups.length === 0) {
      return buildTree(null);
    }

    return rootGroups.flatMap((rootGroup) => (rootGroup.operationGroupId ? buildTree(rootGroup.operationGroupId) : []));
  }, [operationGroups, operations, searchResults]);

  return { treeData };
}
